import React, { useContext, useEffect, useState, useMemo, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { findIconDefinition } from "@fortawesome/fontawesome-svg-core";
import AppContext from "context/Context";
import { useCallingStore } from "../top/store/store";
import { useRoutePermissions } from "modules/user_management/store/permissionsStore";

const AUTO_CLOSE_DELAY = 2000;

const NavbarVerticalMenu = ({ routes, onPanelChange }) => {
  const {
    voitekkCalling,
    exotelCalling,
    knowlarityCalling,
    exotelAmeyoCalling,
    agamiCalling,
    ubonaCalling,
  } = useCallingStore();
  const [routeAccess, setRouteAccess] = useState(
    voitekkCalling ||
      exotelCalling ||
      knowlarityCalling ||
      exotelAmeyoCalling ||
      agamiCalling ||
      ubonaCalling
  );
  const {
    config: { showBurgerMenu },
    setConfig,
  } = useContext(AppContext);
  const { filterRoutesByPermissions, routePermissions, isLoading } =
    useRoutePermissions();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Use index-based selection to handle duplicate names (e.g. two "Order Management" items)
  const [selectedIdx, setSelectedIdx] = useState(null);
  // Controls CSS visibility of the panel (separate from whether a parent with children is selected)
  const [panelVisible, setPanelVisible] = useState(true);
  const timerRef = useRef(null);

  const filteredRoutes = useMemo(() => {
    if (isLoading) return [];
    return filterRoutesByPermissions(routes);
  }, [routes, filterRoutesByPermissions, routePermissions, isLoading]);

  useEffect(() => {
    setRouteAccess(voitekkCalling || exotelCalling || knowlarityCalling);
  }, [voitekkCalling, exotelCalling, knowlarityCalling]);

  // Extract just the pathname portion from a route's "to" (strips query params)
  const getPathOnly = (to) => (to || '').split('?')[0];

  // ── Timer helpers ──
  const clearAutoCloseTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startAutoCloseTimer = useCallback(() => {
    clearAutoCloseTimer();
    timerRef.current = setTimeout(() => {
      setPanelVisible(false);
    }, AUTO_CLOSE_DELAY);
  }, [clearAutoCloseTimer]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => clearAutoCloseTimer();
  }, [clearAutoCloseTimer]);

  // Auto-select the parent whose child matches the current URL on navigation
  useEffect(() => {
    if (!filteredRoutes.length) return;
    // First try exact pathname match (stripping query params from child.to)
    let idx = filteredRoutes.findIndex(
      (r) => r.children && r.children.some((c) => getPathOnly(c.to) === pathname)
    );
    // Fallback: prefix match for nested paths (e.g. pathname starts with child path)
    if (idx === -1) {
      idx = filteredRoutes.findIndex(
        (r) => r.children && r.children.some((c) => {
          const childPath = getPathOnly(c.to);
          return childPath && pathname.startsWith(childPath + '/');
        })
      );
    }
    // Also match leaf routes (no children) by exact path or prefix
    if (idx === -1) {
      idx = filteredRoutes.findIndex(
        (r) => !r.children && r.to && (getPathOnly(r.to) === pathname || pathname.startsWith(getPathOnly(r.to) + '/'))
      );
    }
    if (idx !== -1) {
      setSelectedIdx(idx);
    }
  }, [filteredRoutes, pathname]);

  // Determine if panel should show (active route has children)
  const activeRoute = selectedIdx !== null ? filteredRoutes[selectedIdx] : null;
  const hasChildren = !!(activeRoute && activeRoute.children);

  // Compute effective panel open state: has children AND is visible
  const isPanelOpen = hasChildren && panelVisible;

  // Notify parent about panel visibility
  useEffect(() => {
    if (onPanelChange) onPanelChange(isPanelOpen);
  }, [isPanelOpen, onPanelChange]);

  const closeBurger = () => {
    if (showBurgerMenu) setConfig("showBurgerMenu", false);
  };

  const handleLeafNav = (to) => {
    if (routeAccess) {
      alert("Call is not disposed");
      return;
    }
    setSelectedIdx(null);
    setPanelVisible(false);
    clearAutoCloseTimer();
    navigate(to);
    closeBurger();
  };

  // Only one item highlighted at a time
  const isItemActive = (r, idx) => {
    if (selectedIdx !== null) return idx === selectedIdx;
    if (!r.children) {
      const rPath = getPathOnly(r.to);
      return rPath === pathname || pathname.startsWith(rPath + '/');
    }
    return false;
  };

  // ── Hover handlers ──
  // Hover opens/closes instantly (no timer). Timer only used for menu clicks.
  const handleRailMouseEnter = () => {
    clearAutoCloseTimer();
    if (hasChildren) {
      setPanelVisible(true);
    }
  };

  const handlePanelMouseEnter = () => {
    clearAutoCloseTimer();
  };

  const handleSidebarMouseLeave = () => {
    clearAutoCloseTimer();
    setPanelVisible(false);
  };

  return (
    <div className="sidebar__columns" onMouseLeave={handleSidebarMouseLeave}>
      {/* ── Left: Icon Rail ── */}
      <div
        className={classNames("sidebar__rail", {
          "sidebar__rail--full": !isPanelOpen,
        })}
        onMouseEnter={handleRailMouseEnter}
      >
        {filteredRoutes.map((r, idx) => (
            <div
              key={`${r.name}-${idx}`}
              className={classNames("sidebar__rail-item", {
                "sidebar__rail-item--active": isItemActive(r, idx),
              })}
              onClick={() => {
                if (r.children) {
                  setSelectedIdx(idx);
                  setPanelVisible(true);
                  startAutoCloseTimer();
                  const firstChild = r.children[0];
                  if (firstChild && firstChild.to && !routeAccess) {
                    navigate(firstChild.to);
                    closeBurger();
                  }
                } else {
                  handleLeafNav(r.to);
                }
              }}
            >
              <span className="sidebar__rail-icon">
                {r.icon && findIconDefinition({ prefix: "fas", iconName: r.icon }) ? (
                  <FontAwesomeIcon icon={r.icon} />
                ) : (
                  <span className="sidebar__rail-letter">
                    {r.name.charAt(0)}
                  </span>
                )}
              </span>
              <span className="sidebar__rail-label">{r.name}</span>
            </div>
          ))}
      </div>

      {/* ── Right: Children Panel — CSS-hidden when not visible for smooth transitions ── */}
      {hasChildren && (
        <div
          className={classNames("sidebar__panel", {
            "sidebar__panel--hidden": !panelVisible,
          })}
          onMouseEnter={handlePanelMouseEnter}
        >
          <div className="sidebar__panel-head">{activeRoute.name}</div>
          <div className="sidebar__panel-body">
            {activeRoute.children.map((child) => {
              const childPath = getPathOnly(child.to);
              const isChildActive = childPath === pathname || pathname.startsWith(childPath + '/');
              return (
              <NavLink
                key={child.name}
                end={child.exact}
                to={routeAccess ? "#" : child.to}
                className={() =>
                  classNames("sidebar__panel-link", {
                    "sidebar__panel-link--active":
                      isChildActive && child.to !== "#!",
                  })
                }
                onClick={(e) => {
                  if (routeAccess) {
                    e.preventDefault();
                    alert("Call is not disposed");
                  }
                  // Reset timer on child click (user is interacting)
                  startAutoCloseTimer();
                  closeBurger();
                }}
              >
                {child.icon && (
                  <FontAwesomeIcon
                    icon={child.icon}
                    className="sidebar__panel-link-icon"
                  />
                )}
                <span className="sidebar__panel-link-text">
                  {child.name}
                </span>
              </NavLink>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

NavbarVerticalMenu.propTypes = {
  routes: PropTypes.array.isRequired,
  onPanelChange: PropTypes.func,
};

export default NavbarVerticalMenu;
